/**
 * Convert value of select/radio/checkbox field to an attribute name part that can be used in conditional/final redirect
 * @param inputValue
 * @returns {string}
 */
const getRedirectValueAttributeName = (inputValue) => {
  return 'redirect-' + inputValue.replaceAll(/[,. !?'`]/g, '-').replace(/-+/g, '-').toLowerCase()
}

/**
 * Get a redirect based on selected value. Returns null if no conditional redirect
 * https://github.com/FullstackAcademy/production.fullstackacademy.com/issues/19
 *
 * TODO: unify with final redirect, can be handled by the same code
 *
 * @param formElement
 * @returns {string|null}
 */
export const getConditionalRedirectUrl = (formElement) => {
  let redirect = null;

  // text fields, note the redirect will work for hidden fields too, unlike selects and radio below
  formElement
    .querySelectorAll('input[conditional-redirect=true][type=text]')
    .forEach(conditionalField => {
      if (!redirect) {
        const redirectIfNotEmpty = conditionalField.getAttribute("redirect-if-not-empty")
        if (redirectIfNotEmpty && conditionalField.value !== "") {
          redirect = redirectIfNotEmpty
          return
        }

        const redirectIfEmpty = conditionalField.getAttribute("redirect-if-empty")
        if (redirectIfEmpty && conditionalField.value === "") {
          redirect = redirectIfEmpty
          return
        }

        const redirectAttributeName = getRedirectValueAttributeName(conditionalField.value)
        const redirectValue = conditionalField.getAttribute(redirectAttributeName)
        if (redirectValue) {
          redirect = redirectValue
        }
      }
    })

  // find first conditional select that has value causing a redirect and return it
  formElement
    .querySelectorAll('select[conditional-redirect=true]')
    .forEach(conditionalSelect => {
      // redirect works if it's visible or has input attribute redirect-active-if-hidden=true
      const redirectActiveIfHidden = conditionalSelect.getAttribute("redirect-active-if-hidden")
      if (!redirect && (redirectActiveIfHidden || conditionalSelect.offsetWidth > 0 || conditionalSelect.offsetHeight > 0)) {
        const redirectAttributeName = getRedirectValueAttributeName(conditionalSelect.value)
        const redirectValue = conditionalSelect.getAttribute(redirectAttributeName)
        if (redirectValue) {
          redirect = redirectValue
        }
      }
    })

  formElement
    .querySelectorAll('input[conditional-redirect=true]:checked')
    .forEach(conditionalField => {
      // redirect works if it's visible or has input attribute redirect-active-if-hidden=true
      const redirectActiveIfHidden = conditionalField.getAttribute("redirect-active-if-hidden")
      if (!redirect && (redirectActiveIfHidden || conditionalField.offsetWidth > 0 || conditionalField.offsetHeight > 0)) {
        let redirectValue
        switch (conditionalField.type) {
          case 'checkbox':
            redirectValue = conditionalField.getAttribute('redirect-checked')
            break;

          case 'radio':
            const redirectAttributeName = getRedirectValueAttributeName(conditionalField.value)
            redirectValue = conditionalField.getAttribute(redirectAttributeName)
            break;
        }

        if (redirectValue) {
          redirect = redirectValue
        }
      }
    })

  return redirect
}

/**
 * Get redirect based on conditionals to be used after the form is finally submitted. If null, no redirect found.
 * https://fullstackacademy.monday.com/boards/2335260787/pulses/2721778617
 *
 * @param formElement
 * @returns {null}
 */
export const getFinalRedirectOverwrite = (formElement) => {
  let redirect = null;

  // text fields, note the redirect will work for hidden fields too, unlike selects and radio below
  formElement
    .querySelectorAll('input[conditional-final-redirect=true][type=text]')
    .forEach(conditionalField => {
      if (!redirect) {
        const redirectIfNotEmpty = conditionalField.getAttribute("redirect-if-not-empty")
        if (redirectIfNotEmpty && conditionalField.value !== "") {
          redirect = redirectIfNotEmpty
          return
        }

        const redirectIfEmpty = conditionalField.getAttribute("redirect-if-empty")
        if (redirectIfEmpty && conditionalField.value === "") {
          redirect = redirectIfEmpty
          return
        }

        const redirectAttributeName = getRedirectValueAttributeName(conditionalField.value)
        const redirectValue = conditionalField.getAttribute(redirectAttributeName)
        if (redirectValue) {
          redirect = redirectValue
        }
      }
    })

  // find first conditional select that has value causing a redirect and return it
  formElement
    .querySelectorAll('select[conditional-final-redirect=true]')
    .forEach(conditionalSelect => {
      if (!redirect) {
        // TODO: unify the way hidden redirects are handled: currently hidden selects will work, while checkboxes
        // not unless with redirect-active-if-hidden
        //const redirectActiveIfHidden = conditionalSelect.getAttribute("redirect-active-if-hidden")
        const redirectAttributeName = getRedirectValueAttributeName(conditionalSelect.value)
        const redirectValue = conditionalSelect.getAttribute(redirectAttributeName)
        if (redirectValue) {
          redirect = redirectValue
        }
      }
    })

  formElement
    .querySelectorAll('input[conditional-final-redirect=true]:checked')
    .forEach(conditionalField => {
      const redirectActiveIfHidden = conditionalField.getAttribute("redirect-active-if-hidden")
      if (!redirect && (redirectActiveIfHidden || conditionalField.offsetWidth > 0 || conditionalField.offsetHeight > 0)) {
        let redirectValue
        switch (conditionalField.type) {
          case 'checkbox':
            redirectValue = conditionalField.getAttribute('redirect-checked')
            break;

          case 'radio':
            const redirectAttributeName = getRedirectValueAttributeName(conditionalField.value)
            redirectValue = conditionalField.getAttribute(redirectAttributeName)
            break;
        }

        if (redirectValue) {
          redirect = redirectValue
        }
      }
    })

  return redirect
}

/**
 *
 * @param formElement
 * @param event
 * @returns {boolean}
 */
export const handleImmediateRedirectBasedOnFormValues = (formElement, event) => {
  const conditionalRedirect = getConditionalRedirectUrl(formElement)

  const eventData = event.data
  eventData.append('hubspotutk', window.fullstackacademy.getCookie('hubspotutk'))
  eventData.append('fromHandle', event.form.dataset['handle'])

  if (conditionalRedirect) {
    fetch('/actions/freeformhubspotformswebhookmodule/default/hs-submit', {
      method: "POST",
      body: eventData,
      processData: false,
      headers: {
        //"Content-Type": 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
      },
      credentials: "same-origin"
    })
      .then(() => {
        const queryString = event.form.fullstackParams.toString()
        window.location.href = conditionalRedirect + (queryString ? '?' + queryString : '')
      })
    return true
  }
  return false
}

export const lockSubmit = function (form) {
  const submitButton = form.querySelector('[type=submit]')
  submitButton.disabled = true
  submitButton.classList.add('disabled:opacity-50', 'disabled:cursor-not-allowed')
}

export const unlockSubmit = function (form) {
  const submitButton = form.querySelector('[type=submit]')
  submitButton.disabled = false
  submitButton.classList.remove('disabled:opacity-50', 'disabled:cursor-not-allowed')
}

/**
 * Select option from a <select> element. It supports situations in which there are multiple options with the same
 * value, in this case the first not disabled one will be selected.
 *
 * @param selectElement
 * @param value
 */
export const chooseSelectOption = function (selectElement, value) {
  if (!selectElement) {
    return false
  }
  const allOptions = selectElement.querySelectorAll('option')
  Array.from(allOptions)
    .forEach(option => option.removeAttribute('selected'));
  const visibleOptions = selectElement.querySelectorAll(':scope > option')
  const indexToSelect = Array.from(visibleOptions)
    .findIndex(option => option.value == value && !option.disabled && !option.hidden);
  if (indexToSelect >= 0) {
    selectElement.selectedIndex = indexToSelect;
    return true
  }
}

export const chooseRadioOption = function (radioElements, value) {
  if (!radioElements) {
    return false
  }

  const radioButton = Array.from(radioElements).find((radio) => radio.value === value && !radio.disabled);
  if (radioButton) {
    Array.from(radioElements).forEach((radio) => (radio.checked = false))
    radioButton.checked = true
    return true
  }
  return false
}

function decorateUrlWithTrackingData(urlString) {
  let ga = window[window['GoogleAnalyticsObject']];
  if (ga && typeof ga.getAll === 'function') {
    let tracker = ga.getAll()[0]; // Uses the first tracker created on the page
    urlString = (new window.gaplugins.Linker(tracker)).decorate(urlString);
  }
  return urlString;
}

/**
 * Add string query params to url
 */
export const addQueryStringToUrl = function (url, params) {
  if (!params) {
    return url;
  }

  if (!url.includes('?')) {
    return url + '?' + params;
  } else {
    return url + '&' + params;
  }
}

/**
 * Get input field value. It supports radio buttons in the following way: providing any
 * radio button input field will return checked value for this name in given form.
 * @param inputField
 * @returns {null|*}
 */
export const getInputValue = function (inputField) {
  switch (inputField.type || null) {
    case 'radio':
      if (inputField.checked) {
        return inputField.value
      }

      const checkedField = inputField.closest("form")
        .querySelector(`input[type="radio"][name=${inputField.name}]:checked`)
      if (checkedField) {
        return checkedField.value
      }
      return null;

    default:
      return inputField.value || null
  }
}

export const getDefaultFormParams = function (formElement) {
  // load params from parent element that's passed in the form block object.
  const parent = formElement.parentNode;
  let formParams = {};
  if (parent.hasAttribute('data-form-params')) {
    formParams = JSON.parse(parent.getAttribute('data-form-params'));
  }

  if (!formParams['default:course_preference']) {
    // if there's no course preference, set directly in the form block, try to get one from the meta tag
    const meta = document.querySelector('meta[name="dl-program"]');
    if (meta) {
      const programTranslationTable = {
        web: 'coding',
        dat: 'data',
        cyb: 'cyber',
        pdm: 'prodman',
        dvo: 'devops',
        aim: 'aiml',
        aiml: 'aiml',
        clo: 'cloud',
        cloud: 'cloud',
      }
      const pageProgram = meta.getAttribute('content');
      if (pageProgram && programTranslationTable[pageProgram]) {
        formParams['default:course_preference'] = programTranslationTable[pageProgram];
      }
    }
  }
  return formParams;
}

/**
 * Get external redirect from a 3rd party partner if the returnUrl matches a pattern.
 *
 * @param returnUrl original redirect or URL fetched from the partner
 * @param urlParams
 */
export const addPartnerParameterToUrlParams = async function (returnUrl, partner) {
  // using a very relaxed match in case there are subdomains added to onward.guide domain
  if (!returnUrl.match(/onward\.guide/) && !returnUrl.match(/viewdo\.run\//)) {
    return returnUrl;
  }

  // get fields that were saved in storage on form submit
  const formFieldsStorage = window.fullstackacademy.formFieldsStorage();

  partner = partner || siteBrand.toLowerCase()
  let coursePreference = (formFieldsStorage.getField('course_preference') || '').toLowerCase()

  if (partner === 'osu') {
    // special case for OSU https://fullstackacademy.monday.com/boards/2335260787/pulses/3894491131/posts/1946290703
    coursePreference = 'data'
  }

  // map course preference values so it matches 3rd party requirements
  const coursePreferenceMapping = {
    "data science": "data",
    "Cybersecurity": "cyber"
  }
  coursePreference = coursePreferenceMapping[coursePreference] || coursePreference;

  const body = {
    partner: partner,
    email: formFieldsStorage.getField('email'),
    firstname: formFieldsStorage.getField('firstname'),
    lastname: formFieldsStorage.getField('lastname'),
    phone: formFieldsStorage.getField('phone'),
    explicit_consent: true,
    course_preference: coursePreference,
    rtc_schedule: formFieldsStorage.getField('rtc_schedule'),
    zip: formFieldsStorage.getField('zip'),
    leadsource: "hubspot",
    leadid: window.fullstackacademy.getCookie('hubspotutk'),
  }

  let callbackUrl;
  if (window.location.hostname.includes('.test') || window.location.hostname.includes('staging')) {
    // staging/local
    callbackUrl = new URL('https://dxp-xapi.stage.viewdo.run/v4/experience/e1-fullstack/org');
  } else {
    callbackUrl = new URL('https://dxp-xapi.archer.viewdo.run/v4/experience/e1-fullstack/org');
  }

  callbackUrl.searchParams.set('usemap', 'fullstack')
  callbackUrl.searchParams.set('ocid', coursePreference)
  const response = await fetch(callbackUrl, {
    method: "POST",
    processData: false,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body)
  });
  const data = await response.json();
  const redirectUrl = data.url || '/thank-you';
  return decorateUrlWithTrackingData(redirectUrl);
}

/**
 * Parse config used in hiding steps in form navigation
 *
 * @param formElement
 * @returns {stepsHidingConfig}
 */

export const parseStepsHidingConfig = function (formElement) {
  let stepsHidingConfig = {}
  let rawConfigs = []
  for (const formAttribute of formElement.attributes) {
    if (formAttribute.name.includes('when--')) {
      rawConfigs.push(formAttribute)
    }
  }
  for (const rawConfig of rawConfigs) {
    const fieldName = rawConfig.name.replace('when--', '')
    stepsHidingConfig[fieldName] = {}
    const fieldValues = rawConfig.value
    const arraySortedByFieldName = fieldValues.split(' ')
    for (const fieldValue of arraySortedByFieldName) {
      const fieldValuesArray = fieldValue.split(':')
      stepsHidingConfig[fieldName][fieldValuesArray[0]] = fieldValuesArray[2].split(',').map(function (step) {
        return Number(step)
      })
    }
  }
  return stepsHidingConfig
}

/**
 * Hide steps in form navigation for certain input values
 *
 * @param formElement
 */

export const filterStepsInFormNavigation = function (formElement) {
  for (const fieldName in formElement.stepsHidingConfig) {
    const fieldElement = formElement.querySelector(`[name="${fieldName}"]`)
    if (fieldElement) {
      const fieldValue = window.fullstackacademy.getInputValue(fieldElement)
      initializeStepsHiding(formElement, formElement.stepsHidingConfig[fieldName], fieldValue)
    }
  }
}

function initializeStepsHiding(formElement, stepsHidingConfig, selectedValue) {
  //when there are more fields with conditions defined in one form, the styles will be set only for the last field
  let stylesheet = document.getElementById(`${formElement.id}-styles`)
  if (stylesheet) {
    document.head.removeChild(stylesheet)
  }
  stylesheet = document.createElement('style')
  stylesheet.appendChild(document.createTextNode(''))
  stylesheet.id = `${formElement.id}-styles`
  document.head.appendChild(stylesheet)

  if (stepsHidingConfig[selectedValue]) {
    stepsHidingConfig[selectedValue].forEach(index => {
      stylesheet.sheet.insertRule(`#${formElement.id} .fsa__form-step:nth-of-type(${index}) { display: none; }`, 0)
      stylesheet.sheet.insertRule(`#${formElement.id} .fsa__form-chevron:nth-of-type(${index}) { display: none; }`, 0)
      //hide second to last cheveron when last step will be hidden
      if (formElement.querySelector('[data-pages-count]').dataset.pagesCount == index) {
        stylesheet.sheet.insertRule(`#${formElement.id} .fsa__form-chevron:nth-of-type(${index - 1}) { display: none; }`, 0)
      }
    })
  }
}

/**
 * Filter step bars in form for certain input values
 *
 * @param formElement
 */

export const filterStepBarsInForm = function (formElement) {
  const stepBarsContainer = formElement.querySelector('.fsa__form-stepbars')
  if (stepBarsContainer) {
    const visibleSteps = Array.from(formElement.querySelectorAll('.fsa__form-stepbars .fsa__form-step')).filter(
      (stepElement) => window.getComputedStyle(stepElement).display !== 'none'
    )
    const activeStepElement = stepBarsContainer.querySelector('.active')
    const currentStepIndex = visibleSteps.indexOf(activeStepElement) + 1

    activeStepElement.innerText = `Step ${currentStepIndex} of ${visibleSteps.length}`

    //hide container if only one step is visible
    if (visibleSteps.length === 1) {
      stepBarsContainer.style.display = 'none'
    }
  }
}

/**
 * Call local API to get eligible status and marketing location for user filling form
 *
 * @param params
 * @returns {Promise} Promise represents object with action, eligible and marketingLocation properties
 */
export const checkIfUserIsEligible = function (params) {
  return new Promise((resolve, reject) => {
    if (params.zipCode && (params.isStep2 || (!params.isCountrySelectVisible && params.areAllRequiredFieldsFilledInFlag))) {
      if (params.isVetTecFieldVisible && params.vetTec == 'Yes') {
        //any lead who answers that they are eligible for VET TEC funding should be eligible
        resolve({
          action: 'continue',
          eligible: true,
        })
      } else {
        // check if zip code is US and matches to a no-fly state
        fetch(`${sharedApiUrl}/zip-mapper/v2/get-mapping.php?zip=${params.zipCode}&brand=${siteBrand}&program=${params.coursePreference}&schedule=${params.rtcSchedule}`, {
          method: 'GET',
        })
          .then(response => response.json())
          .then(response => {
            if (response.success == true) {
              if (response.data) {
                if (response.data.is_no_fly_zone) {
                  resolve({
                    action: 'continueAsIneligible',
                    eligible: false,
                    marketingLocation: response.data.marketing_location
                  })
                } else {
                  resolve({
                    action: 'continue',
                    eligible: true,
                    marketingLocation: response.data.marketing_location
                  })
                }
              } else if (response.unknown_program) {
                resolve({
                  action: 'continue',
                  eligible: true
                })
              }
            } else {
              resolve({
                action: 'getAdditionalData'
              })
            }
          })
          .catch((error) => {
            console.error('Error:', error)
          })
      }
    } else if (params.isStep2 || (params.isCountrySelectVisible && params.areAllRequiredFieldsFilledInFlag)) {
      if (params.isVetTecFieldVisible && params.vetTec == 'Yes') {
        //any lead who answers that they are eligible for VET TEC funding should be eligible
        resolve({
          action: 'continue',
          eligible: true,
        })
      } else if (params.isTuitionFieldVisible && params.tuition == 'true') {
        //if a user is prompted to answer if they can afford the full tuition, and they answer yes, they should be eligible
        resolve({
          action: 'continue',
          eligible: true,
        })
      } else if (params.country && params.country != 'United States') {
        resolve({
          action: 'continueAsIneligible',
          eligible: false,
          marketingLocation: 'outside-US'
        })
      } else {
        // check if selected state matches to a no-fly state
        fetch(`${sharedApiUrl}/zip-mapper/v2/get-mapping.php?state=${params.state}&brand=${siteBrand}&program=${params.coursePreference}&schedule=${params.rtcSchedule}`, {
          method: 'GET',
        })
          .then(response => response.json())
          .then(response => {
            if (response.success == true) {
              if (response.data) {
                if (response.data.is_no_fly_zone) {
                  resolve({
                    action: 'continueAsIneligible',
                    eligible: false,
                    marketingLocation: response.data.marketing_location
                  })
                } else {
                  resolve({
                    action: 'continue',
                    eligible: true,
                    marketingLocation: response.data.marketing_location
                  })
                }
              } else if (response.unknown_program) {
                //Iowa applicants are BLOCKED from enrolling in any online courses, even for unknown programs
                if (params.state == 'IA') {
                  resolve({
                    action: 'continueAsIneligible',
                    eligible: false,
                  })
                } else {
                  resolve({
                    action: 'continue',
                    eligible: true,
                  })
                }
              }
            } else {
              resolve({
                action: 'continueAsIneligible',
                eligible: false,
                marketingLocation: 'DNM'
              })
            }
          })
          .catch((error) => {
            console.error('Error:', error)
            return false
          })
      }
    } else {
      resolve({
        action: 'continue',
      })
    }
  })
}

export const storeDataStorageBeforeRedirect = function (params) {
  return new Promise((resolve, reject) => {
    fetch(`${sharedApiUrl}/data-storage/store.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(params),
    }).then(response => response.json())
      .then(response => {
        console.log(response)
        resolve({
          action: 'continue',
          token: response.token
        })
      })
      .catch((error) => {
        console.error('Error:', error)
        return false
      })
  })
}

export const getDataStorageAfterRedirect = async function (params) {
  return await fetch(`${sharedApiUrl}/data-storage/get.php`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(params),
  }).then(response => response.json())
    .then(response => {
      console.log(response)
      return JSON.parse(response.data)
    })
    .catch((error) => {
      console.error('Error:', error)
      return false
    })
}